import SEO from "@/components/site/SEO";
import PageHero from "@/components/site/PageHero";
import CTASection from "@/components/site/CTASection";
import FAQSection from "@/components/site/FAQSection";
import { FAQS_TRAC_CMS } from "@/data/content";

const CORE_CAPABILITIES = [
  {
    title: "Competence records",
    body:
      "Maintain structured competence profiles for drivers, instructors, managers and safety-critical personnel, with clear visibility of current status, evidence and review history.",
  },
  {
    title: "Assessment planning",
    body:
      "Support scheduled assessments, development reviews, observations and assurance activity so competence cycles are visible, consistent and easier to manage.",
  },
  {
    title: "Evidence management",
    body:
      "Store supporting evidence, observations, actions, outcomes and audit trails in one place, reducing reliance on disconnected spreadsheets, folders and email chains.",
  },
  {
    title: "Risk-based oversight",
    body:
      "Help leaders identify emerging competence risks, overdue activity, repeated trends, development needs and areas requiring additional assurance.",
  },
  {
    title: "Compliance reporting",
    body:
      "Produce clearer visibility for internal governance, leadership review, audit preparation and external assurance conversations.",
  },
  {
    title: "Role-based access",
    body:
      "Support controlled visibility across teams, managers and administrators so information is accessible to the right people without unnecessary exposure.",
  },
];

const WHO_FOR = [
  "Train operating companies",
  "Freight operating companies",
  "Heritage and light rail organisations",
  "Rail training providers",
  "Driver management teams",
  "Operational standards and assurance teams",
];

const PROBLEMS = [
  {
    title: "Competence evidence is scattered",
    body:
      "Records often sit across spreadsheets, shared drives, paper files, emails and local trackers, making it difficult to see the full competence picture quickly.",
  },
  {
    title: "Managers lack live visibility",
    body:
      "Operational leaders need to know who is current, who is overdue, who requires development and where assurance attention should be focused.",
  },
  {
    title: "Audit preparation takes too long",
    body:
      "When evidence is fragmented, preparing for internal review, external assurance or regulatory scrutiny becomes slower and more resource-intensive.",
  },
  {
    title: "Risk signals can be missed",
    body:
      "Competence management should not only confirm completion. It should help identify patterns, repeated themes, emerging risk and development priorities.",
  },
];

const ASSURANCE_POINTS = [
  {
    title: "Audit-ready evidence",
    body:
      "Designed to make competence decisions easier to evidence, review and explain.",
  },
  {
    title: "Controlled access",
    body:
      "Supports a more disciplined approach to information access, role separation and management oversight.",
  },
  {
    title: "Clear accountability",
    body:
      "Helps show who completed an assessment, what evidence was considered, what decision was made and what action followed.",
  },
  {
    title: "ISO 27001-aligned thinking",
    body:
      "Built with information security, access control, audit evidence, supplier assurance and governance expectations in mind.",
  },
];

const WORKFLOW = [
  {
    step: "01",
    title: "Build the competence profile",
    body:
      "Create structured records for individuals, roles, competence requirements, assessment cycles and supporting evidence.",
  },
  {
    step: "02",
    title: "Capture assessment activity",
    body:
      "Record assessments, observations, development reviews, action plans and evidence in a consistent format.",
  },
  {
    step: "03",
    title: "Monitor status and risk",
    body:
      "Track competence status, overdue activity, development needs, assurance themes and risk indicators across teams.",
  },
  {
    step: "04",
    title: "Evidence assurance",
    body:
      "Use structured reporting and audit trails to support leadership review, compliance checks and assurance conversations.",
  },
];

export default function TRACCms() {
  return (
    <>
      <SEO
        title="TRAC-CMS | Railway Competence Management System | UKTDA"
        description="TRAC-CMS is a railway competence management system supporting competence records, assessments, evidence, audit trails, risk visibility and compliance assurance for safety-critical teams."
        path="/trac-cms"
      />

      <PageHero
        eyebrow="TRAC-CMS"
        title="Railway competence management with evidence, visibility and assurance built in."
        intro="TRAC-CMS helps railway organisations manage competence records, assessment cycles, evidence, development actions, audit trails and risk-based assurance in one structured platform."
      >
        <div className="flex items-center gap-4">
  <div className="flex h-24 w-24 md:h-32 md:w-32 items-center justify-center rounded-2xl bg-white p-3 shadow-sm ring-1 ring-slate-200">
    <img
      src="/TRAC-CMS.svg"
      alt="TRAC-CMS logo"
      className="max-h-full max-w-full object-contain"
    />
  </div>
</div>
      </PageHero>

      <section className="py-16 md:py-24 bg-white" data-testid="trac-cms-intro">
        <div className="container-rail">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-start">
            <div>
              <p className="label-eyebrow text-safety">Competence Management System</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-5xl text-navy leading-tight">
                Move beyond spreadsheets, disconnected evidence and reactive compliance.
              </h2>

              <p className="mt-6 text-lg text-slate-700 leading-relaxed">
                Competence management in safety-critical environments needs more than a record of
                completed assessments. It needs structured evidence, clear ownership, live visibility,
                consistent review cycles and the ability to identify risk before it becomes a problem.
              </p>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-6 md:p-8">
              <p className="label-eyebrow text-safety">Built for railway assurance</p>

              <h3 className="mt-3 font-display font-bold text-2xl text-navy">
                A single view of competence, evidence and risk.
              </h3>

              <p className="mt-4 text-slate-700 leading-relaxed">
                TRAC-CMS brings together the information leaders need to answer practical assurance
                questions quickly:
              </p>

              <ul className="mt-6 space-y-3 text-slate-700">
                <li className="flex gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full bg-safety shrink-0" />
                  <span>Who is currently competent, restricted, overdue or under development?</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full bg-safety shrink-0" />
                  <span>What evidence supports each competence decision?</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full bg-safety shrink-0" />
                  <span>Where are repeated themes, trends or risk indicators appearing?</span>
                </li>
                <li className="flex gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full bg-safety shrink-0" />
                  <span>What needs action before the next audit, review or assurance cycle?</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50" data-testid="trac-cms-problems">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">The problem</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Competence assurance becomes harder when the evidence is fragmented.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              Many organisations already have competent people, robust standards and experienced
              managers. The challenge is often the system around them: visibility, consistency,
              evidence control and the ability to demonstrate assurance clearly.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {PROBLEMS.map((item) => (
              <div
                key={item.title}
                className="rounded-md border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="font-display font-bold text-xl text-navy">
                  {item.title}
                </h3>

                <p className="mt-3 text-slate-600 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="trac-cms-capabilities">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Core capabilities</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              A practical platform for competence records, assessments and assurance evidence.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              TRAC-CMS is designed around the realities of operational management: evidence needs to
              be easy to find, assessments need to be visible, actions need to be tracked and leaders
              need confidence that competence risk is being managed proactively.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CORE_CAPABILITIES.map((item) => (
              <article
                key={item.title}
                className="rounded-md border border-slate-200 bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="h-10 w-10 rounded-md bg-safety/10 border border-safety/20 flex items-center justify-center">
                  <span className="text-safety font-bold">✓</span>
                </div>

                <h3 className="mt-5 font-display font-bold text-xl text-navy">
                  {item.title}
                </h3>

                <p className="mt-3 text-slate-600 leading-relaxed">
                  {item.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-navy text-white" data-testid="trac-cms-workflow">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">How it supports assurance</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl leading-tight">
              From individual competence records to organisational assurance.
            </h2>

            <p className="mt-4 text-white/75 leading-relaxed">
              TRAC-CMS is structured to support the full competence management cycle, from creating
              clear records through to capturing evidence, monitoring risk and demonstrating
              compliance.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {WORKFLOW.map((item) => (
              <div
                key={item.step}
                className="rounded-md border border-white/15 bg-white/5 p-6"
              >
                <div className="text-safety font-display font-bold text-3xl">
                  {item.step}
                </div>

                <h3 className="mt-5 font-display font-bold text-xl">
                  {item.title}
                </h3>

                <p className="mt-3 text-white/70 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white" data-testid="trac-cms-who-for">
        <div className="container-rail">
          <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
            <div>
              <p className="label-eyebrow text-safety">Who it is for</p>

              <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
                Built for teams responsible for safety-critical competence.
              </h2>

              <p className="mt-4 text-slate-600 leading-relaxed">
                TRAC-CMS is suitable for organisations that need to manage competence evidence,
                assessment records, development actions and assurance reporting across operationally
                critical roles.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {WHO_FOR.map((item) => (
                <div
                  key={item}
                  className="rounded-md border border-slate-200 bg-slate-50 p-5"
                >
                  <p className="font-semibold text-navy">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-50" data-testid="trac-cms-assurance">
        <div className="container-rail">
          <div className="max-w-3xl">
            <p className="label-eyebrow text-safety">Governance and information security</p>

            <h2 className="mt-4 font-display font-bold text-3xl md:text-4xl text-navy leading-tight">
              Designed with assurance, auditability and information control in mind.
            </h2>

            <p className="mt-4 text-slate-600 leading-relaxed">
              Competence records can contain sensitive operational, employment and performance
              information. TRAC-CMS is therefore positioned around controlled access, clear audit
              trails, evidence integrity and security-conscious governance.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {ASSURANCE_POINTS.map((item) => (
              <div
                key={item.title}
                className="rounded-md border border-slate-200 bg-white p-6 shadow-sm"
              >
                <h3 className="font-display font-bold text-xl text-navy">
                  {item.title}
                </h3>

                <p className="mt-3 text-slate-600 leading-relaxed">
                  {item.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-md border border-slate-200 bg-white p-6 md:p-8">
            <p className="label-eyebrow text-safety">Enterprise readiness</p>

            <h3 className="mt-3 font-display font-bold text-2xl text-navy">
              Supporting safer decisions, better records and stronger assurance conversations.
            </h3>

            <p className="mt-4 text-slate-700 leading-relaxed max-w-4xl">
              TRAC-CMS is not simply a storage tool. It is intended to support a more mature
              competence management environment where operational leaders can evidence decisions,
              review risk, monitor action, prepare for audit and demonstrate that competence is being
              managed in a structured and defensible way.
            </p>
          </div>

        </div>
      </section>

      <FAQSection
        items={FAQS_TRAC_CMS}
        title="TRAC-CMS FAQs"
        intro="Common questions about competence records, evidence management, compliance visibility and safety-critical assurance."
        schemaId="faq-trac-cms"
      />

      <CTASection
        eyebrow="Competence assurance"
        title="Build a clearer competence management system for your railway team."
        intro="Tell us about your current competence records, assessment process, evidence challenges and assurance requirements. We can help identify how TRAC-CMS could support your operational context."
        primary={{
          label: "Discuss TRAC-CMS",
          to: "/contact",
          testId: "trac-cms-cta-contact",
        }}
        secondary={{
          label: "View TRAC",
          to: "/trac",
          testId: "trac-cms-cta-trac",
        }}
      />
    </>
  );
}